import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentRoom: {
    chatMessages: []
  },
  loading: false,
  error: null,
  showBackgoundImageEffect: false,
  remoteBackgroundEffects: {},
  participantNetworkStats: {}, // { [sessionId]: { bitrateKbps, strength, isOnline } }
  localParticipantStatus: null,
};

const getMessageTimestamp = (message = {}) =>
  message.timestamp || message.createdAt || message.sentAt || '';

const getMessageTime = (message = {}) => {
  const value = new Date(getMessageTimestamp(message)).getTime();
  return Number.isFinite(value) ? value : 0;
};

const normalizeParticipants = (participants = []) => {
  const seenDevices = new Set();
  const seenSessions = new Set();
  const result = [];
  for (const participant of [...participants].reverse()) {
    if (!['waiting', 'admitted', 'presenting'].includes(participant?.status)) {
      continue;
    }
    const deviceId = String(participant?.deviceId || '').trim();
    const sessionId = String(participant?.sessionId || '').trim();
    if (
      (deviceId && seenDevices.has(deviceId)) ||
      (sessionId && seenSessions.has(sessionId))
    ) {
      continue;
    }
    if (deviceId) seenDevices.add(deviceId);
    if (sessionId) seenSessions.add(sessionId);
    result.push(participant);
  }
  return result.reverse();
};


const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setRoom: (state, action) => {
        const payload = action.payload || {};
  const safeRoomId =
    payload.roomId || payload.id || payload._id || payload.code || payload.slug;

  state.currentRoom = {
    ...payload,
    roomId: safeRoomId,                          // ✅ always set
    chatMessages: payload.chatMessages || [],
    participants: normalizeParticipants(payload.participants || []),
  };
      state.loading = false;
      state.error = null;
      state.participantNetworkStats = state.participantNetworkStats || {};
    },
    addParticipant: (state, action) => {
      const { participant, currentUserId, sessionId, socketId } = action.payload;
      if (!state.currentRoom) return;
    
      if (!state.currentRoom.participants) {
        state.currentRoom.participants = [];
      }
    
      const uid = participant.userId || participant.user?._id || participant.user;
      const exists = state.currentRoom.participants.find((p) => {
        const pid = p.userId || p.user?._id || p.user;
        return (
          String(pid || '') === String(uid || '') &&
          String(p.sessionId || '') === String(participant.sessionId || '')
        );
      });
    
      const isSelf =
        (uid === currentUserId && participant.sessionId === sessionId) ||
        participant.socketId === socketId;
    
      if (exists && !isSelf) {
        Object.assign(exists, {
          ...participant,
          userId: uid,
          username:
            participant.username ||
            participant.user?.username ||
            exists.username ||
            'Unknown',
          disconnected: false,
          disconnectedAt: null,
        });
      } else if (!isSelf) {
        const deviceId = String(participant.deviceId || '').trim();
        if (deviceId) {
          state.currentRoom.participants = state.currentRoom.participants.filter(
            (item) => String(item.deviceId || '').trim() !== deviceId
          );
        }
        const username = participant.username || participant.user?.username || 'Unknown';
        state.currentRoom.participants.push({ ...participant, userId: uid, username });
      }
    }
    
    ,
    // Example: in roomSlice.js (Redux Toolkit or similar)
updateParticipantBackground: (state, action) => {
  const { sessionId, effect } = action.payload;
  if (!state.remoteBackgroundEffects) state.remoteBackgroundEffects = {};
  state.remoteBackgroundEffects[sessionId] = effect;
},
    setRemoteBackgroundEffect(state, action) {
      const { sessionId, effectType } = action.payload;
      state.remoteBackgroundEffects[sessionId] = effectType;
    },
    clearRemoteBackgroundEffects(state) {
      state.remoteBackgroundEffects = {};
    },
    showBackgoundImageEffectStatus(state, action)
    {
      state.showBackgoundImageEffect = action.payload
    },
    setLocalParticipantStatus: (state, action) => {
      const status =
        typeof action.payload === 'string'
          ? action.payload
          : (typeof action.payload?.status === 'string' ? action.payload.status : undefined);
      if (status) state.localParticipantStatus = status;
      else if (typeof action.payload === 'object') state.localParticipantStatus = action.payload;
    },


   updateParticipantStatus: (state, action) => {
      const { participantId, userId, sessionId, status } = action.payload || {};
      if (!state?.currentRoom?.participants) return;
      const participant = state.currentRoom.participants.find(
        (p) =>
          p._id === participantId ||
          ((p.userId === userId || p.user?._id === userId || p.user === userId) && p.sessionId === sessionId)
      );
      if (participant && typeof status === 'string') participant.status = status;
    },
     updateParticipantNetworkStats(state, action) {
      const {
        sessionId,
        bitrateKbps,
        bitrate,
        audioBitrateKbps,
        videoBitrateKbps,
        screenBitrateKbps,
        packetLoss,
        rttMs,
        jitterMs,
        connectionState,
        media,
        sampledAt,
        strength,
        isOnline,
      } = action.payload || {};
      if (!sessionId) return;

      if (!state.participantNetworkStats) state.participantNetworkStats = {};
      if (!state.participantNetworkStats[sessionId]) state.participantNetworkStats[sessionId] = {};
      const cur = state.participantNetworkStats[sessionId];

      let kbps =
        typeof bitrateKbps === 'number' ? bitrateKbps
        : (typeof bitrate === 'number' ? bitrate
        : (typeof bitrate === 'string' ? parseFloat(bitrate) : undefined));
      if (typeof kbps === 'number' && !Number.isNaN(kbps)) cur.bitrateKbps = Math.round(kbps);
      if (bitrateKbps === null) cur.bitrateKbps = null;

      if (Number.isFinite(audioBitrateKbps)) {
        cur.audioBitrateKbps = Math.round(audioBitrateKbps);
      }
      if (audioBitrateKbps === null) cur.audioBitrateKbps = null;
      if (Number.isFinite(videoBitrateKbps)) {
        cur.videoBitrateKbps = Math.round(videoBitrateKbps);
      }
      if (videoBitrateKbps === null) cur.videoBitrateKbps = null;
      if (Number.isFinite(screenBitrateKbps)) {
        cur.screenBitrateKbps = Math.round(screenBitrateKbps);
      }
      if (screenBitrateKbps === null) cur.screenBitrateKbps = null;
      if (Number.isFinite(packetLoss)) {
        cur.packetLoss = Number(Math.max(0, packetLoss).toFixed(1));
      }
      if (packetLoss === null) cur.packetLoss = null;
      if (Number.isFinite(rttMs)) cur.rttMs = Math.max(0, Math.round(rttMs));
      if (rttMs === null) cur.rttMs = null;
      if (Number.isFinite(jitterMs)) {
        cur.jitterMs = Math.max(0, Math.round(jitterMs));
      }
      if (jitterMs === null) cur.jitterMs = null;
      if (typeof connectionState === 'string') cur.connectionState = connectionState;
      if (media && typeof media === 'object') cur.media = media;
      if (Number.isFinite(sampledAt)) cur.sampledAt = sampledAt;
      if (typeof strength === 'number')  cur.strength = strength;
      if (typeof isOnline === 'boolean') cur.isOnline = isOnline;
    },
    updateParticipant: (state, action) => {
      const { participantId, updates } = action.payload;
      const participant = state.currentRoom?.participants?.find(
        p => p.sessionId === participantId // ensure this is by sessionId if that's your unique key
      );
      if (participant) {
        Object.assign(participant, updates);
      }
    },
    removeParticipant: (state, action) => {
      const { userId, sessionId } = action.payload;
      if (state.currentRoom && state.currentRoom.participants) {
        state.currentRoom.participants = state.currentRoom.participants.filter(p =>
          String(p.sessionId || '') !== String(sessionId || '') &&
          !(
            (p.userId === userId || p.user?._id === userId || p.user === userId) &&
            (!sessionId || p.sessionId === sessionId)
          )
        );
      }
      if (sessionId && state.participantNetworkStats) {
        delete state.participantNetworkStats[String(sessionId)];
      }
    },
    removeParticipantNetworkStats: (state, action) => {
      const sessionId =
        typeof action.payload === 'string'
          ? action.payload
          : action.payload?.sessionId;
      if (sessionId && state.participantNetworkStats) {
        delete state.participantNetworkStats[String(sessionId)];
      }
    },
    
    addChatMessage: (state, action) => {
      if (!state.currentRoom) return;
      if (!state.currentRoom.chatMessages) state.currentRoom.chatMessages = [];

      const exists = state.currentRoom.chatMessages.some(
        (msg) =>
          (
            (msg._id || msg.id) &&
            String(msg._id || msg.id) ===
              String(action.payload._id || action.payload.id)
          ) ||
          (String(msg.userId || '') === String(action.payload.userId || '') &&
            msg.message === action.payload.message &&
            Math.abs(
              getMessageTime(msg) - getMessageTime(action.payload)
            ) < 1000)
      );

      if (!exists) {
        state.currentRoom.chatMessages.push(action.payload);
        state.currentRoom.chatMessages.sort((a, b) =>
          getMessageTime(a) - getMessageTime(b)
        );
      }
    },

    updateChatMessage: (state, action) => {
      const { id, updates } = action.payload;
      if (!state.currentRoom?.chatMessages) return;

      let index = state.currentRoom.chatMessages.findIndex(
        (msg) => msg.id === id || msg._id === id
      );

      if (
        index === -1 &&
        String(id || '').startsWith('temp-') &&
        updates._id
      ) {
        index = state.currentRoom.chatMessages.findIndex(
          (msg) =>
            msg.id?.startsWith('temp-') &&
            msg.userId === updates.userId &&
            msg.message === updates.message
        );
      }

      if (index >= 0) {
        state.currentRoom.chatMessages[index] = {
          ...state.currentRoom.chatMessages[index],
          ...updates,
          id: updates._id,
          _id: updates._id,
        };
      }
    },
    setNewHost: (state, action) => {
      state.currentRoom.host = action.payload;
    },
    clearRoom: (state) => {
      state.currentRoom = null;
      state.loading = false;
      state.error = null;
      state.participantNetworkStats = {};
      state.localParticipantStatus = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setRoom,
  addParticipant,
  updateParticipantStatus,
  addChatMessage,
  updateChatMessage,
  clearRoom,
  setLocalParticipantStatus,
  updateParticipant,
  updateParticipantNetworkStats,
  removeParticipantNetworkStats,
  removeParticipant,
  setNewHost,
  setRemoteBackgroundEffect,
  clearRemoteBackgroundEffects,
  showBackgoundImageEffectStatus,
  updateParticipantBackground
} = roomSlice.actions;

export default roomSlice.reducer;
